import {catchError, EMPTY, filter, Observable, throwError} from 'rxjs';

import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {ApplicationRef, inject, Injectable, Injector, isDevMode, signal, WritableSignal} from '@angular/core';
import {Router} from '@angular/router';
import {ApiNames, environment} from '@shared-types';
import {LoaderService, ToastService} from '@utils';

import {ApiErrorModel} from './data/api-error.model';
import {ApiErrorObject, APIErrorsStruct} from './data/api-error.types';
import errors from './data/api-errors.json';

const SKIP_API_ERROR_CODES = [
	// List of error codes that should not trigger a toast message
	'400', // Bad Request
	'401', // Unauthorized
	'403', // Forbidden
	'404', // Not Found
	'500', // Internal Server Error
	// Add more codes as needed
];

/**
 * This interceptor is used to handle app known API errors and show a toast message to the user.
 */
@Injectable()
export class ApiInterceptor implements HttpInterceptor {
	/* -----------------------------------------------  Constants ----------------------------------------------- */
	public readonly API_ERRORS$: WritableSignal<Map<string, ApiErrorObject>> = signal(new Map<string, ApiErrorObject>());
	private readonly injector = inject(Injector);

	/* -----------------------------------------------  Providers ----------------------------------------------- */
	private readonly toastService: ToastService = inject(ToastService);
	private readonly loaderService: LoaderService = inject(LoaderService);
	private readonly appRef$ = inject(ApplicationRef);
	private readonly router: Router = inject(Router);

	/* -----------------------------------------------  Constructor ----------------------------------------------- */
	constructor() {
		this._loadApiErrorMessages();
	}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (isDevMode()) {
			// add the tenant to the request headers:
			const tenant = environment.company;
			req = req.clone({
				setHeaders: {
					// 'X-Tenant': tenant,
				},
			});
		}
		return next.handle(req).pipe(
			filter(ev => !(ev instanceof HttpErrorResponse)), // Filter out HttpErrorResponse events
			catchError(error => {
				if (SKIP_API_ERROR_CODES.includes(error.status.toString())) {
					// Skip showing toast for these error codes
					return throwError(() => error);
				}
				const apiError = ApiErrorModel.fromErrorObject(error);

				// Handle the error based on its type/properties
				if (apiError.apiErrorObject && apiError.apiErrorObject.catchError) {
					// Call your error handling logic here
					return this.handleApiError(apiError);
				}

				// Re-throw the error if catchError is false
				return throwError(() => apiError);
			}),
		);
	}

	// Define your error handling method
	private handleApiError(apiError: ApiErrorModel): Observable<never> {
		// Check if we need to navigate based on onError.navTo
		if (apiError.apiErrorObject.onError?.navTo) {
			this.router.navigate([apiError.apiErrorObject.onError.navTo]);
		}

		ApiErrorModel.showToast(apiError.apiErrorObject, this.injector);
		return EMPTY;
	}

	/* ------------------------------------------------  Methods ------------------------------------------------ */
	private _loadApiErrorMessages(): void {
		const errorsJSON: APIErrorsStruct = Object.assign({}, errors as unknown as APIErrorsStruct);
		Object.entries(errorsJSON.errors).forEach(([apiName, apiErrors]) => {
			const apiName_ = apiName as ApiNames;
			Object.entries(apiErrors).forEach(([errorMessage, errorData], index) => {
				this.API_ERRORS$().set(errorMessage, {
					errorMessage,
					id: errorData?.id || index,
					userMessage: errorData.userMessage,
					apiName: apiName_,
					catchError: errorData.catchError,
					onError: errorData.onError,
				});
			});
		});
	}
}
